import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { BLOG_POSTS } from "../data/blog";
import { SERVICES } from "../data/services";

export default function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);
    if (!post) return <Navigate to="/blog" replace />;
    const idx = BLOG_POSTS.indexOf(post);
    const related = BLOG_POSTS.filter((_, i) => i !== idx).slice(0, 2);
    const relatedServices = [SERVICES[idx % SERVICES.length], SERVICES[(idx + 4) % SERVICES.length], SERVICES[(idx + 8) % SERVICES.length]];

    return (
        <div data-testid={`blog-post-${post.slug}`}>
            <Seo title={`${post.title} | Xtreme Protection Scotland`} description={post.description} />

            <section className="relative overflow-hidden" data-testid="blog-post-hero">
                <div className="absolute inset-0">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#07080B]" />
                </div>
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-40 pb-16">
                    <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 font-mono-xp text-[11px] uppercase tracking-[0.25em] text-gray-400 hover:text-[#E53E3E] transition-colors mb-8">
                        <ArrowLeft size={13} /> All Intel
                    </Link>
                    <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-[#E53E3E] mb-4">{post.category}</p>
                    <h1 className="font-display uppercase font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[0.95] text-white" data-testid="blog-post-title">
                        {post.title}
                    </h1>
                    <div className="flex items-center gap-4 mt-6 font-mono-xp text-[11px] uppercase tracking-widest text-gray-500">
                        <span className="flex items-center gap-1.5"><Calendar size={12} /> {new Date(post.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
                        <span className="flex items-center gap-1.5"><Clock size={12} /> {post.readTime}</span>
                    </div>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
                {post.sections.map((s, i) => (
                    <Reveal key={i} className="mb-10">
                        {s.h && (
                            <h2 className="font-display uppercase font-bold text-2xl tracking-wide text-white mb-4">{s.h}</h2>
                        )}
                        {s.ps.map((p, j) => (
                            <p key={j} className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">{p}</p>
                        ))}
                    </Reveal>
                ))}

                <Reveal className="mt-14 bg-[#0F1117] border border-white/10 p-7" data-testid="blog-related-services">
                    <h2 className="font-display uppercase font-bold text-xl tracking-wide text-white mb-5">Related security services</h2>
                    <ul className="space-y-3">
                        {relatedServices.map((s) => (
                            <li key={s.slug}>
                                <Link to={`/services/${s.slug}`} data-testid={`blog-service-link-${s.slug}`} className="group flex items-center justify-between text-sm text-gray-400 hover:text-[#E53E3E] transition-colors">
                                    {s.name} Scotland
                                    <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal className="mt-10">
                    <h2 className="font-display uppercase font-bold text-xl tracking-wide text-white mb-5">Keep reading</h2>
                    <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {related.map((r) => (
                            <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`} className="group block bg-[#0F1117] hover:bg-[#161922] transition-colors p-6">
                                <span className="font-mono-xp text-[10px] uppercase tracking-[0.2em] text-[#E53E3E]">{r.category}</span>
                                <h3 className="font-display uppercase font-bold text-lg tracking-wide text-white group-hover:text-[#E53E3E] transition-colors mt-2 leading-tight">{r.title}</h3>
                            </Link>
                        ))}
                    </div>
                </Reveal>
            </section>

            <CtaBand />
        </div>
    );
}
